import { createCrudStore } from "@/store/crudStore";
import { apiService } from "@/services/api";
import db from "@/services/offlineDb";

// ============ SIEMBRAS ============
const siembrasStore = createCrudStore("siembras");

const siembras = {
  ...siembrasStore,

  state: {
    ...siembrasStore.state,
    inventario: [],
    comprasInsumo: [],
  },

  getters: {
    ...siembrasStore.getters,
    siembras(state) {
      return Array.isArray(state.siembras) ? state.siembras : [];
    },
    inventario(state) {
      return Array.isArray(state.inventario) ? state.inventario : [];
    },
    comprasInsumo(state) {
      return Array.isArray(state.comprasInsumo) ? state.comprasInsumo : [];
    },
  },

  mutations: {
    ...siembrasStore.mutations,
    setInventario(state, inventario) {
      state.inventario = inventario;
    },
    setComprasInsumo(state, compras) {
      state.comprasInsumo = compras;
    },
  },

  actions: {
    ...siembrasStore.actions,

    // ============ SIEMBRAS ============
    async getSiembras({ commit, rootState }) {
      commit("setLoading", true);
      commit("clearError");
      try {
        // Traer TODAS las siembras (incluyendo cosechadas/inactivas)
        const response = await apiService.getCustom(
          "siembras/?solo_activas=false"
        );
        if (response.status >= 200 && response.status < 300) {
          commit("setState", response.data);
          return response.data;
        }
      } catch (error) {
        if (error.response?.status === 403) {
          commit("setError", null);
          commit("setState", []);
          return [];
        }
        commit("setError", error.message || "Error obteniendo siembras");
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
    deleteSiembra: siembrasStore.actions.delete,

    async postSiembra({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.create("siembras", payload),
        onSuccessCallback: async (commit, data) => {
          commit("addItem", data);
          // Refrescar inventario porque se descuentan insumos
          await dispatch("getInventario");
          console.log("✅ Siembra creada correctamente");
        },
        errorMessage: "Error al crear siembra",
      });
    },

    async patchSiembra({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.update("siembras", payload.id, payload),
        onSuccessCallback: (commit, data) => {
          commit("updateItem", data);
          console.log("✅ Siembra actualizada correctamente");
        },
        errorMessage: "Error al editar siembra",
      });
    },

    // ============ INVENTARIO ============
    async getInventario({ dispatch, commit }) {
      try {
        return await dispatch("executeApiCall", {
          apiCallFunction: () => apiService.getCustom("inventario-siembra"),
          onSuccessCallback: (commit, data) => {
            commit("setInventario", data);
            // Guardar en Dexie para offline
            if (Array.isArray(data) && data.length > 0) {
              db.inventario_siembra.bulkPut(data).catch(() => {});
            }
            console.log("✅ Inventario cargado:", data.length);
          },
          errorMessage: "Error al cargar inventario",
        });
      } catch (err) {
        if (!navigator.onLine) {
          console.log(
            "📴 Offline fallback: leyendo inventario_siembra de Dexie"
          );
          const local = await db.inventario_siembra.toArray();
          commit("setInventario", local);
          return local;
        }
        throw err;
      }
    },

    async postInventario({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("inventario-siembra", payload),
        onSuccessCallback: async () => {
          await dispatch("getInventario");
          console.log("✅ Insumo agregado al inventario");
        },
        errorMessage: "Error al crear insumo en inventario",
      });
    },

    async patchInventario({ dispatch }, { id, nueva_cantidad }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(`inventario-siembra/${id}`, {
            cantidad: nueva_cantidad,
          }),
        onSuccessCallback: async () => {
          await dispatch("getInventario");
          console.log("✅ Stock de inventario actualizado");
        },
        errorMessage: "Error al actualizar stock",
      });
    },

    // Alias para patchInventario (usado desde ForrajesView)
    async patchInventarioStock({ dispatch }, payload) {
      return dispatch("patchInventario", {
        id: payload.id,
        nueva_cantidad: payload.cantidad_actual,
      });
    },

    // Eliminar insumo del inventario
    async deleteInventario({ dispatch }, id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`inventario-siembra/${id}`),
        onSuccessCallback: async () => {
          await dispatch("getInventario");
          console.log("✅ Insumo eliminado del inventario");
        },
        errorMessage: "Error al eliminar insumo",
      });
    },

    // ============ COMPRAS ============
    async getCompras({ dispatch }, inventarioId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`inventario-siembra/${inventarioId}/compras`),
        onSuccessCallback: (commit, data) => {
          commit("setComprasInsumo", data);
          console.log("✅ Compras cargadas:", data.length);
        },
        errorMessage: "Error al cargar compras",
      });
    },

    async postCompra({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("inventario-siembra/compras", payload),
        onSuccessCallback: async () => {
          await Promise.all([
            dispatch("getInventario"),
            dispatch("getCompras", payload.inventario_id),
          ]);
          console.log("✅ Compra de insumo registrada");
        },
        errorMessage: "Error al registrar compra",
      });
    },

    async deleteCompra({ dispatch }, { id, inventario_id }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`insumos-siembra/compras/${id}`),
        onSuccessCallback: async () => {
          await Promise.all([
            dispatch("getInventario"),
            dispatch("getCompras", inventario_id),
          ]);
          console.log("✅ Compra de insumo eliminada");
        },
        errorMessage: "Error al eliminar compra",
      });
    },

    // Alias para postCompra (usado desde ForrajesView)
    async postCompraInventario({ dispatch }, payload) {
      return dispatch("postCompra", payload);
    },

    // ============ LLUVIAS ============
    async getLluvias({ commit }, siembraId) {
      const { data } = await apiService.getCustom(
        `siembras/${siembraId}/lluvias`
      );
      return data;
    },

    async sincronizarLluviasDesdeLote({ dispatch }, siembraId) {
      const { data } = await apiService.postCustom(
        `siembras/${siembraId}/sincronizar-lluvias-lote`,
        {}
      );
      // Refrescar siembras para que el total de lluvia se actualice
      await dispatch("getSiembras");
      return data;
    },

    async postLluvia({ dispatch }, { siembraId, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`siembras/${siembraId}/lluvias`, payload),
        onSuccessCallback: async () => {
          await dispatch("getSiembras");
          console.log("✅ Lluvia registrada");
        },
        errorMessage: "Error al registrar lluvia",
      });
    },

    async deleteLluvia({ dispatch }, lluviaId) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`siembras/lluvias/${lluviaId}`),
        onSuccessCallback: async () => {
          await dispatch("getSiembras");
          console.log("✅ Lluvia eliminada");
        },
        errorMessage: "Error al eliminar lluvia",
      });
    },

    async getEstadisticasLluvias(_, filtros = {}) {
      const params = new URLSearchParams();
      if (filtros.anio) params.append("anio", filtros.anio);
      if (filtros.material_id)
        params.append("material_id", filtros.material_id);
      const { data } = await apiService.getCustom(
        `siembras/estadisticas/lluvias?${params}`
      );
      return data;
    },

    // ============ SEGUROS SIEMBRA ============
    async getSeguros(_, siembraId) {
      const { data } = await apiService.getCustom(
        `siembras/${siembraId}/seguros`
      );
      return data;
    },

    async postSeguro(_, { siembraId, seguro }) {
      const { data } = await apiService.postCustom(
        `siembras/${siembraId}/seguros`,
        seguro
      );
      return data;
    },

    async putSeguro(_, { siembraId, seguroId, seguro }) {
      const { data } = await apiService.putCustom(
        `siembras/${siembraId}/seguros/${seguroId}`,
        seguro
      );
      return data;
    },

    async deleteSeguro(_, { siembraId, seguroId }) {
      await apiService.deleteCustom(
        `siembras/${siembraId}/seguros/${seguroId}`
      );
    },

    // ============ LLUVIA GENERAL ============
    async postLluviaGeneral({ dispatch }, payload) {
      const { data } = await apiService.postCustom(
        "siembras/lluvias/general",
        payload
      );
      await dispatch("getSiembras");
      return data;
    },
  },
};

export default siembras;
